import { 
  FundingRateSimple, 
  LongShortRatioData, 
  LiquidationData, 
  OpenInterestData,
  FearGreedData 
} from '../types/coinglass';

export type { 
  FundingRateSimple, 
  LongShortRatioData, 
  LiquidationData, 
  OpenInterestData,
  FearGreedData 
} from '../types/coinglass';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Parse data column — Supabase stores it as a JSON string
function parseData(row: any): any {
  if (!row.data) return null;
  return typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
}

async function supabaseQuery(table: string, params: string = '') {
  const url = `${SUPABASE_URL}/rest/v1/${table}${params ? '?' + params : ''}`;
  const response = await fetch(url, {
    headers: {
      'apikey': SUPABASE_KEY,
      'Authorization': `Bearer ${SUPABASE_KEY}`,
      'accept': 'application/json'
    }
  });
  if (!response.ok) return null;
  return response.json();
}

export async function fetchFundingRates(symbol: string = 'BTC'): Promise<FundingRateSimple[]> {
  const data = await supabaseQuery(
    'market_data',
    `symbol=eq.${symbol}&data_type=eq.funding_rate&order=created_at.desc&limit=50`
  );
  if (!data || !Array.isArray(data)) return [];

  const rates: FundingRateSimple[] = [];
  for (const row of data) {
    const d = parseData(row);
    if (d && d.exchange) {
      rates.push({
        exchangeName: (d.exchange || 'Unknown').toUpperCase(),
        fundingRate: parseFloat(d.funding_rate) || 0,
        nextFundingTime: d.next_funding_time || 0,
        predicatedRate: 0,
        symbol: row.symbol || symbol
      });
    }
  }
  return rates;
}

export async function fetchLongShortRatio(
  symbol: string = 'BTCUSDT', 
  _interval: string = '4h', 
  limit: number = 1
): Promise<LongShortRatioData | null> {
  const normalizedSymbol = symbol.replace('USDT', '').replace('USDC', '');
  const data = await supabaseQuery(
    'market_data',
    `symbol=eq.${normalizedSymbol}&data_type=eq.long_short_ratio&order=created_at.desc&limit=${limit}`
  );
  if (!data || !Array.isArray(data) || data.length === 0) return null;

  const latest = data[0];
  const d = parseData(latest);
  if (!d) return null;
  
  return {
    symbol: normalizedSymbol,
    timestamp: new Date(latest.created_at).getTime(),
    longRatio: parseFloat(d.longRatio || d.long_ratio || '0'),
    shortRatio: parseFloat(d.shortRatio || d.short_ratio || '0')
  };
}

export async function fetchLiquidations(_timeType: string = '24h'): Promise<LiquidationData[]> {
  const data = await supabaseQuery(
    'market_data',
    `symbol=eq.BTC&data_type=eq.liquidation&order=created_at.desc&limit=10`
  );
  if (!data || !Array.isArray(data)) return [];

  return data
    .filter((row: any) => row.data)
    .map((item: any) => {
      const d = parseData(item);
      return {
        symbol: item.symbol || 'BTC',
        liquidation_usd_24h: parseFloat(d?.liquidation_usd_24h || d?.liquidation || '0'),
        long_liquidation_usd_24h: parseFloat(d?.long_liquidation_usd_24h || '0'),
        short_liquidation_usd_24h: parseFloat(d?.short_liquidation_usd_24h || '0'),
        liquidation_usd_12h: 0,
        long_liquidation_usd_12h: 0,
        short_liquidation_usd_12h: 0,
        longVolUsd: parseFloat(d?.longVolUsd || '0'),
        shortVolUsd: parseFloat(d?.shortVolUsd || '0'),
        totalVolUsd: parseFloat(d?.totalVolUsd || '0'),
        longLiquidationUsd: parseFloat(d?.longLiquidationUsd || '0'),
        shortLiquidationUsd: parseFloat(d?.shortLiquidationUsd || '0'),
        totalLiquidationUsd: parseFloat(d?.totalLiquidationUsd || '0')
      };
    });
}

export async function fetchOpenInterest(symbol: string = 'BTC'): Promise<OpenInterestData[]> {
  const data = await supabaseQuery(
    'market_data',
    `symbol=eq.${symbol}&data_type=eq.open_interest&order=created_at.desc&limit=20`
  );
  if (!data || !Array.isArray(data)) return [];

  return data
    .filter((row: any) => row.data)
    .map((item: any) => {
      const d = parseData(item);
      return {
        symbol: symbol.toUpperCase(),
        exchangeName: d?.exchangeName || d?.exchange || 'Unknown',
        openInterest: parseFloat(d?.openInterest || d?.open_interest || '0'),
        oiChangePercent: parseFloat(d?.oiChangePercent || d?.oi_change_percent || '0'),
        volume24h: parseFloat(d?.volume24h || d?.volume_24h || '0')
      };
    });
}

export async function fetchFearGreedIndex(): Promise<FearGreedData[]> {
  const data = await supabaseQuery(
    'fear_greed_history',
    'order=created_at.desc&limit=10'
  );
  if (!data || !Array.isArray(data)) return [];

  return data.map((entry: any) => ({
    value: entry.value,
    valueClassification: entry.value_classification || 'Neutral',
    timestamp: new Date(entry.created_at).getTime()
  }));
}

export async function fetchSupportedCoins(): Promise<string[]> {
  return ['BTC', 'ETH', 'SOL', 'XRP', 'ADA', 'DOGE', 'LINK', 'AVAX'];
}

export const coinglassAPI = {
  fetchFundingRates,
  fetchLongShortRatio,
  fetchLiquidations,
  fetchOpenInterest,
  fetchFearGreedIndex,
  fetchSupportedCoins
};
